import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, Check, X, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import FlowLoader from "./FlowLoader";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { conveyorLogger } from "./helpers/logHandler";
import { zoneDispatchReasonKey } from "./sorterTwo/zoneDispatchReasons";
import { apiFetch } from "../api/apiFetch";

type CompleteChute = {
    axiomId: number;
    chute: string;
    palletId: string;
    system: string;
    timeStamp: string;
    tag: string;
    type: string;
    receivedItems: boolean;
    collected: boolean;
    processed: string;
};

type ConveyorCollectChuteProps = {
    chute: CompleteChute;
    chuteScanned: boolean;
    goBack: () => void;
};

const ConveyorCollectChute = ({ chute, chuteScanned, goBack }: ConveyorCollectChuteProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [step, setStep] = useState<number>(chuteScanned ? 2 : 1);
    const [scan, setScan] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const [destination, setDestination] = useState<string>("");
    const [destinationBarcode, setDestinationBarcode] = useState<string>("");
    const [checkingZone, setCheckingZone] = useState<boolean>(false);
    const [newChute, setNewChute] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const warehouse = chute.palletId.startsWith("UK") ? "Newport" : chute.palletId.startsWith("EU") ? "Poland" : "Test";

    const collectChute = async (): Promise<boolean> => {
        try {
            const res = await apiFetch(`/conveyor/chutes/complete/finish`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    warehouse: warehouse,
                    axiomId: chute.axiomId,
                    chute: chute.chute
                })
            });

            if (res.ok) {
                conveyorLogger(warehouse, "Remove Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "success");
                return true;
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
                return false;
            } else {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("collect_failed"));
                conveyorLogger(warehouse, "Remove Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "error");
                return false;
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("collect_failed"));
            conveyorLogger(warehouse, "Remove Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "error");
            return false;
        }
    };

    const doScanChute = useCallback((scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const correctedScan = Number(val.slice(2)).toString();

        if (correctedScan.trim() !== chute.chute) {
            playError();
            toast.dismiss(); toast.error(t("wrong_chute"));
            setScan("");
            conveyorLogger(warehouse, "Scanned Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "error");
            return;
        }

        conveyorLogger(warehouse, "Scanned Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "success");
        setScan("");
        setStep(2);
    }, [scan, chute.chute, t]);

    const doScanTag = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        if (val.trim() !== chute.palletId) {
            playError();
            toast.dismiss(); toast.error(t("wrong_pallet"));
            setScan("");
            conveyorLogger(warehouse, "Scanned Pallet", val.trim(), `Chute ${chute.chute}`, "Hand", "wrong scan");
            return;
        }

        const tag = val.trim();
        setScan("");

        conveyorLogger(warehouse, "Scanned Pallet", val.trim(), `Chute ${chute.chute}`, "Hand", "success");

        // Collect the chute, then call check-chute to get zone assignment
        setStep(3);
        setCheckingZone(true);

        await apiFetch(`/conveyor/chutes/complete/collect`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                warehouse: warehouse,
                axiomId: chute.axiomId,
                palletId: chute.palletId,
                system: chute.system,
                tag: chute.palletId
            })
        }).then(async (res) => {
            const data = await res.json();

            console.log(data);

            if (res.ok && data.location) {
                setDestination(data.location.name);
                setDestinationBarcode(data.location.barcode);
                setCheckingZone(false);
                conveyorLogger(warehouse, "Collect Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "success");
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("collect_failed"));
                setCheckingZone(false);
                conveyorLogger(warehouse, "Collect Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "error");
                setStep(2);
            }
        }).catch(() => {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("collect_failed"));
            setCheckingZone(false);
            conveyorLogger(warehouse, "Collect Pallet From Chute", chute.palletId, `Chute ${chute.chute}`, "Hand", "error");
            setStep(2);
        });
    }, [scan, chute, warehouse, navigator, t]);

    const doScanLocation = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        if (val.trim() !== destinationBarcode) {
            playError();
            toast.dismiss(); toast.error(t("wrong_location"));
            setScan("");
            conveyorLogger(warehouse, "Put Pallet In Location", chute.palletId, "Hand", destinationBarcode, "wrong scan");
            return;
        }

        const collectSuccess = await collectChute();
        if (!collectSuccess) {
            setCheckingZone(false);
            setStep(3);
            conveyorLogger(warehouse, "Put Pallet In Location", chute.palletId, "Hand", destinationBarcode, "error");
            return;
        }

        setScan("");
        flashScanStatus("success");
        playSuccess();
        conveyorLogger(warehouse, "Put Pallet In Location", chute.palletId, "Hand", destinationBarcode, "success");
        setStep(4);
    }, [scan, destinationBarcode, t]);

    const doScanNewChute = useCallback((scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        // Check if pallet is valid
        if (!/(^NP|PL|TT)[0-9]{3}$/.test(val.trim())) {
            flashScanStatus("error");
            toast.dismiss(); toast.error(t("invalid_chute"));
            playError();
            conveyorLogger("Newport", "Scanned Chute", val.trim(), "Warehouse", "Hand", "error");
            return;
        }

        setNewChute(val.trim());
        setScan("");
        setStep(6);
    }, [scan]);

    const doScanNewPallet = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const newPalletId = val.trim();
        // Check if pallet is valid
        if (!/(^UK|PL|TT)-[0-9]{4}$/.test(val)) {
            flashScanStatus("error");
            toast.dismiss(); toast.error(t("invalid_pallet"));
            playError();
            conveyorLogger("Newport", "Scanned Pallet", newPalletId, "Warehouse", "Hand", "error");
            return;
        }

        conveyorLogger("Newport", "Scanned Pallet", newPalletId, "Warehouse", "Hand", "success");

        setScan("");
        setSubmitting(true);

        console.log({
            warehouse: newPalletId.startsWith("UK") ? "Newport" : newPalletId.startsWith("PL") ? "Poland" : "Test",
            palletId: newPalletId,
            chute: Number(newChute.slice(2)).toString()
        });

        await apiFetch(`/conveyor/chutes/pallet`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                warehouse: newPalletId.startsWith("UK") ? "Newport" : newPalletId.startsWith("PL") ? "Poland" : "Test",
                palletId: newPalletId,
                chute: Number(newChute.slice(2)).toString()
            })
        }).then(async (res) => {
            if (res.ok) {
                flashScanStatus("success");
                playSuccess();
                conveyorLogger("Newport", "Add Pallet", newPalletId, "Warehouse", `Chute ${newChute}`, "success");
                setTimeout(() => goBack(), 1500);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                // Portal reauction move on: a pallet that is not a free Portal
                // tote is refused with { ok:false, reason } — name it; the
                // operator stays on this step and scans another pallet.
                let body: any = null;
                try { body = await res.json(); } catch { body = null; }
                flashScanStatus("error");
                playError();
                toast.dismiss();
                if (body && body.ok === false && body.reason) {
                    toast.error(t(zoneDispatchReasonKey(body.reason), { status: body.status ?? "", order: "", tote: newPalletId }));
                    conveyorLogger("Newport", "Add Pallet", newPalletId, "Warehouse", `Chute ${newChute}`, body.reason);
                } else {
                    toast.error(t("add_failed"));
                    conveyorLogger("Newport", "Add Pallet", newPalletId, "Warehouse", `Chute ${newChute}`, "error");
                }
            }
        }).catch(() => {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("add_failed"));
            conveyorLogger("Newport", "Add Pallet", newPalletId, "Warehouse", `Chute ${newChute}`, "error");
        });

        setSubmitting(false);
    }, [scan, newChute, navigator, goBack, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        switch (step) {
            case 1: doScanChute(scannedValue); break;
            case 2: doScanTag(scannedValue); break;
            case 3: doScanLocation(scannedValue); break;
            case 5: doScanNewChute(scannedValue); break;
            case 6: doScanNewPallet(scannedValue); break;
        }
    }, [step, doScanChute, doScanTag, doScanLocation, doScanNewChute, doScanNewPallet]);

    const goodsinClass = (chute.system === "goodsin") && step < 4 ? " goodsin-" + chute.type : "";

    return (
        <div className={"scan-panel" + goodsinClass} style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("collect_chute")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {step === 1 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {t("scan_chute_number").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <p>{t("chute_label", { chute: chute.chute })}</p>
                            <p>{t("pallet_label", { id: chute.palletId })}</p>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {t("scan_pallet_tag").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <p>{t("chute_label", { chute: chute.chute })}</p>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="conveyor-scan-group">
                        {checkingZone ? (
                            <FlowLoader />
                        ) : (
                            <>
                                <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                                    {t("take_to_location", { location: destination }).toUpperCase().split(" ").map((word, i) => (
                                        <FitWord key={i}>{word}</FitWord>
                                    ))}
                                </div>
                                <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                                    <p>{t("scan_location_confirm")}</p>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 4 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("add_pallet_back").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className="conveyor-btn-group">
                            <button className="conveyor-yes-btn" onClick={() => setStep(5)}><Check size={24} strokeWidth={3} />{t("yes")}</button>
                            <button className="conveyor-no-btn" onClick={goBack}><X size={24} strokeWidth={3} />{t("no")}</button>
                        </div>
                    </div>
                )}

                {step === 5 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {t("scan_chute").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <p>{t("chute_label", { chute: newChute })}</p>
                        </div>
                    </div>
                )}

                {step === 6 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_pallet_id").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className="scan-feedback-area">
                            {submitting && <p className="conveyor-status-text">{t("submitting")}</p>}
                        </div>
                    </div>
                )}

            </div>
            {scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}
            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={submitting}
            />
        </div>
    );
};

export default ConveyorCollectChute;
